import type { SetLayer } from '@/lib/concept-workbench/types';

/**
 * WHAT WILL THIS PROMPT CHANGE? (spec §4.4.1)
 *
 * The floating bar sits over the artwork, so "submit" is ambiguous unless the
 * target is named. It is named by a chip that always mirrors the canvas
 * selection, and the mapping is decided HERE rather than inside the bar — a
 * classifier that reads free text and guesses between "replace the background"
 * and "rewrite this headline" is the silent-no-op class this repo keeps
 * shipping, and it would spend an image credit to get it wrong.
 *
 * The three targets are genuinely different operations against different
 * endpoints, with different costs:
 *
 *   image  → POST /api/generate-image   — pixels, costs a credit
 *   copy   → POST /api/content/rewrite  — words, no image spend at all
 *   layer  → local property mutation    — no network, no spend
 *
 * NOTE ON THE COMPOSER RULE. Mora's Home chat composer input row is a
 * DO-NOT-CROWD zone and no capability chip may live there. This is a different
 * surface and this is not a capability chip: it is a scope indicator for a
 * destructive, billable operation. Removing it does not simplify the bar, it
 * makes it unsafe. Flagged as open question O3; the founder can overrule.
 */

export type PromptTargetKind = 'image' | 'copy' | 'layer';

/** The six real edit modes. There is NO `variation` — that is a different, legacy endpoint. */
export type ImageEditMode =
  'inpaint' | 'outpaint' | 'recolor' | 'enhance' | 'background-replace' | 'restore-url';

export interface PromptTarget {
  kind: PromptTargetKind;
  /** The chip's label. `Image` when nothing (or the background) is selected. */
  label: string;
  placeholder: string;
  /** Null for the image target — the image is not a store layer. */
  layerId: string | null;
}

export const IMAGE_TARGET: PromptTarget = {
  kind: 'image',
  label: 'Image',
  placeholder: 'Describe a change to this image…',
  layerId: null,
};

export function resolvePromptTarget(selectedLayer: SetLayer | null | undefined): PromptTarget {
  if (!selectedLayer) return IMAGE_TARGET;
  if (selectedLayer.kind === 'text') {
    return {
      kind: 'copy',
      label: selectedLayer.name,
      placeholder: 'Rewrite this text…',
      layerId: selectedLayer.id,
    };
  }
  return {
    kind: 'layer',
    label: selectedLayer.name,
    placeholder: 'Restyle this layer…',
    layerId: selectedLayer.id,
  };
}

export interface PromptModeOption {
  id: string;
  label: string;
  description: string;
  editMode: ImageEditMode;
  /** `n:4` on the "try four versions" row; 1 everywhere else. */
  count: number;
  separatorBefore?: boolean;
  /**
   * What the composer is PREFILLED with when this mode is picked, and what the
   * request carries if the user sends it unedited.
   *
   * 🔴 This closes a live silent no-op. `useWorkbenchPrompt.submit` returns
   * immediately on an empty prompt, so picking `Enhance` from the old `+` menu
   * with nothing typed dispatched NOTHING — no request, no error, no status
   * line. Every mode now arrives with a real instruction the user can edit
   * before sending, which is the same prefill contract MediaStudio's suggestion
   * chips already use.
   */
  defaultPrompt: string;
}

/**
 * The `+` menu. The plain-text path always means `inpaint`; every other mode is
 * reachable ONLY by explicit choice.
 *
 * `outpaint` is here because it already works server-side — `/api/generate-image`
 * composes a purpose-built "extend into ${aspectRatio}" instruction and
 * deliberately classes it as NOT a same-canvas edit. There was nothing to build;
 * there was a button missing. It is the honest answer to "fill this ratio without
 * cropping the subject out of frame".
 */
export const PROMPT_MODES: readonly PromptModeOption[] = [
  {
    id: 'background-replace',
    label: 'Replace the background',
    description: 'Keep the subject, change everything behind it',
    editMode: 'background-replace',
    count: 1,
    defaultPrompt: 'Replace the background, keeping the subject exactly as it is.',
  },
  {
    id: 'recolor',
    label: 'Recolour',
    description: 'Change colours without moving anything',
    editMode: 'recolor',
    count: 1,
    defaultPrompt: 'Change the colours without moving anything in the frame.',
  },
  {
    id: 'enhance',
    label: 'Enhance',
    description: 'Sharpen and clean up the existing image',
    editMode: 'enhance',
    count: 1,
    defaultPrompt: 'Sharpen and clean up this image without changing the composition.',
  },
  {
    id: 'outpaint',
    label: 'Extend to fill this size',
    description: 'Paints new background instead of cropping the subject',
    editMode: 'outpaint',
    count: 1,
    defaultPrompt: 'Extend the scene to fill this size instead of cropping the subject.',
  },
  {
    id: 'four-up',
    label: 'Try four versions',
    description: 'Generate alternatives and pick one',
    editMode: 'inpaint',
    count: 4,
    separatorBefore: true,
    defaultPrompt: 'Try four alternative versions of this image.',
  },
];

export interface ImageRequest {
  prompt: string;
  aspectRatio: string;
  editMode?: ImageEditMode;
  referenceImageUrl?: string;
  referenceImageRoles?: ('product' | 'style' | 'logo' | 'person' | 'untagged')[];
  lookOverride?: 'none';
  n: number;
  angleId?: string | null;
  audienceId?: string | null;
  /**
   * THE DURABLE-CORRELATION FIELDS (LANE IMG-DONE). Present only when this
   * request targets a Concept Workbench rendition (i.e. `briefContext` was
   * supplied — see `buildImageRequest`). Let `/api/generate-image` attach the
   * finished media back onto the exact row that asked for it from INSIDE the
   * endpoint itself, rather than depending solely on the browser still being
   * alive when the provider responds. See `useWorkbenchPrompt.submit` and
   * `attachGeneratedMediaByCorrelation` (ConceptRenditionService.ts).
   */
  renditionId?: string;
  generationToken?: string;
  /**
   * The Concept `attachGeneratedMediaByCorrelation` scopes its match to.
   * `briefContext.conceptId` is embedded in the composed prompt TEXT for the
   * model to read, but that text is not a structured field the server can
   * query against — this carries the same id as a real request field.
   */
  conceptId?: string;
}

export interface BriefConceptImageContext {
  conceptId: string;
  hook: string | null;
  summary: string | null;
  rationale: string | null;
  cta: string | null;
  visualDirection: unknown;
  intents: readonly { intent: 'organic' | 'paid'; platform: string | null }[];
  angleId: string | null;
  audienceId: string | null;
}

export function composeConceptImagePrompt(
  instruction: string,
  context: BriefConceptImageContext,
  options: { supersedeVisualDirection?: boolean } = {},
): string {
  return [
    'Authoritative Brief Concept. This request is limited to the selected rendition only.',
    JSON.stringify({
      conceptId: context.conceptId,
      hook: context.hook,
      summary: context.summary,
      rationale: context.rationale,
      cta: context.cta,
      ...(options.supersedeVisualDirection ? {} : { visualDirection: context.visualDirection }),
      intents: context.intents,
    }),
    `Requested image instruction: ${instruction}`,
  ].join('\n\n');
}

/**
 * Build the generate-image body.
 *
 * `referenceImageUrl` is omitted when the variant has no image yet: every edit
 * mode other than a first generation needs a reference, and sending an undefined
 * one would fail the server's `z.string().url()` rather than fall back. So a
 * first generation is a plain generation and says so.
 */
export function buildImageRequest(args: {
  prompt: string;
  aspectRatio: string;
  currentImageUrl: string | null;
  mode?: PromptModeOption;
  briefContext?: BriefConceptImageContext;
  /** THE rendition this request generates for. See `ImageRequest.renditionId`. */
  renditionId?: string;
  generationToken?: string;
  /** The reference image is the current approved design; the Concept's stored visual direction is dropped from the prompt. */
  supersedeVisualDirection?: boolean;
  /**
   * Re-lay a flat / typographic design at a new ratio: a plain generation with the current card as a
   * `product` reference (tagged, so the screenshot heuristic cannot swallow it), no edit mode (so
   * nothing is outpainted), no saved Look (its photographic style block turned a text card into a
   * desk photo), and no Concept JSON in the prompt (the instruction alone is the spec).
   */
  recompose?: boolean;
}): ImageRequest {
  const { prompt, aspectRatio, currentImageUrl, mode, briefContext, renditionId, generationToken } =
    args;
  const requestPrompt =
    briefContext && !args.recompose
      ? composeConceptImagePrompt(prompt, briefContext, {
          supersedeVisualDirection: args.supersedeVisualDirection,
        })
      : prompt;
  const attribution = briefContext
    ? { angleId: briefContext.angleId, audienceId: briefContext.audienceId }
    : {};
  // Correlation only makes sense alongside briefContext — that's what supplies
  // the conceptId the server needs to find the row (concept + aspect ratio).
  const correlation =
    briefContext && renditionId && generationToken
      ? { renditionId, generationToken, conceptId: briefContext.conceptId }
      : {};
  if (args.recompose && currentImageUrl) {
    return {
      prompt: requestPrompt,
      aspectRatio,
      referenceImageUrl: currentImageUrl,
      referenceImageRoles: ['product'],
      lookOverride: 'none',
      n: 1,
      ...attribution,
      ...correlation,
    };
  }
  if (!currentImageUrl) {
    return {
      prompt: requestPrompt,
      aspectRatio,
      n: mode?.count ?? 1,
      ...attribution,
      ...correlation,
    };
  }
  return {
    prompt: requestPrompt,
    aspectRatio,
    editMode: mode?.editMode ?? 'inpaint',
    referenceImageUrl: currentImageUrl,
    n: mode?.count ?? 1,
    ...attribution,
    ...correlation,
  };
}

/**
 * A first generation cannot be an edit — there is nothing to edit. The menu says
 * so instead of dispatching a doomed request.
 */
export function isModeAvailable(mode: PromptModeOption, hasImage: boolean): boolean {
  if (!hasImage) return false;
  return mode.editMode !== 'restore-url';
}
